import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import LoadingCard from '../components/LoadingCard'
import UpgradePopup from '../components/UpgradePopup'
import ChatMessageItem from '../components/ChatMessageItem'
import isPaidUser from '../utils/isPaidUser'
import { refusalOf, MEMBERS_ONLY } from '../utils/refusal'

import loadingIcon from '../assets/images/loading-dark.svg'

import {
  checkExistingChatRoom,
  createChatRoom,
  createChatMessage,
  createChatRoomUser,
  getChatRoomData,
  chatCreateMessageSubscriptions,
  chatDeleteMessageSubscriptions,
  getChatRoomUsersByChatRoomId,
  updateChatRoomUser,
  updateChatRoomRead,
  deleteChatMessage,
  deleteChatRoomUser,
} from '../actions/chat'
import { memberIdsOf, roomMemberIds, rowsOfRoom } from '../utils/chatMemberIds'

export function ChatRoom(props) {
  const {
    user,
    match,
    getChatRoomData: getChatRoomDataAction,
    chatCreateMessageSubscriptions: createMessageSubscriptionAction,
    chatDeleteMessageSubscriptions: deleteMessageSubscriptionAction,
  } = props

  const [membership, checkMembership] = useState(null)
  const [currentUsername, setCurrentUsername] = useState(null)
  const [showUpgrade, setUpgradePopup] = useState(false)
  const [message, setMessage] = useState('')
  const [newChatRoom, setChatRoom] = useState(match.params)
  const [chatData, getChatData] = useState({})
  const [limit, setLimit] = useState(30)
  const [nextToken, setNextToken] = useState(null)
  const [loading, setLoading] = useState(false)
  const [stopScrollingToBottom, setStopScrollingToBottom] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [deletingMessage, setDeletingMessage] = useState(false)
  // A load that failed for a reason other than a refusal (network, throttling); Retry reloads.
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const createSubscriptionRef = useRef(null)
  const deleteSubscriptionRef = useRef(null)

  useEffect(() => {
    setChatRoom(match.params)
    user.currentUser &&
      checkMembership(
        isPaidUser(user.currentUser.attributes['custom:membership'])
      )
    user.accountData &&
      setCurrentUsername(user.accountData.idToken.payload['cognito:username'])

    const mainHeader = document.getElementById('main_header')
    if (mainHeader) {
      mainHeader.classList.add('sticky-main-header')
    }

    return () => {
      if (mainHeader) {
        mainHeader.classList.remove('sticky-main-header')
      }
    }
  }, [match.params, user.currentUser, user.accountData])

  const scrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight)
  }

  useEffect(() => {
    if (!stopScrollingToBottom) {
      scrollToBottom()
    }
  })

  const loadMoreMessages = () => {
    if (nextToken) {
      setStopScrollingToBottom(true)
      setLoading(true)
      const fetchMoreData = async () => {
        let loadMoreFetchChatData
        try {
          loadMoreFetchChatData = await props.getChatRoomData(
            newChatRoom.chatRoom_uid,
            30,
            nextToken
          )
        } catch (err) {
          console.log('error loading more messages: ', err)
          return
        }
        if (!loadMoreFetchChatData) return
        loadMoreFetchChatData.messages.items.reverse()
        setNextToken(loadMoreFetchChatData.messages.nextToken)
        setLimit(limit + 30)
        loadMoreFetchChatData.messages.items = [
          ...loadMoreFetchChatData.messages.items,
          ...chatData.messages.items,
        ]
        getChatData(loadMoreFetchChatData) // this does not update the front end right away.
      }
      fetchMoreData()
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      let fetchChatData
      setLoadFailed(false)
      try {
        fetchChatData = await getChatRoomDataAction(newChatRoom.chatRoom_uid, limit)
      } catch (err) {
        // Not a refusal (that is null: "No chat found here"): offer a retry.
        console.log('error loading chat room: ', err)
        setLoadFailed(true)
        return
      }
      fetchChatData && fetchChatData.messages.items.reverse()
      fetchChatData && setNextToken(fetchChatData.messages.nextToken)
      getChatData(fetchChatData)
    }
    if (newChatRoom.chatRoom_uid && newChatRoom.chatRoom_uid !== 'new') {
      fetchData()
    }
  }, [getChatRoomDataAction, limit, newChatRoom.chatRoom_uid, loadAttempt])

  useEffect(() => {
    const subscribeChatData = async () => {
      if (!newChatRoom.chatRoom_uid || newChatRoom.chatRoom_uid === 'new') {
        return
      }

      createSubscriptionRef.current = await createMessageSubscriptionAction(
        (createdMessage) => {
          if (
            !createdMessage ||
            createdMessage.messageChatRoomId !== newChatRoom.chatRoom_uid
          ) {
            return
          }
          // Directly merge new message into local chatData so receiver
          // sees it immediately without a full API refetch.
          getChatData((prevChatData) => {
            if (!prevChatData || Object.keys(prevChatData).length === 0) {
              return prevChatData
            }
            const prevItems = prevChatData.messages?.items || []
            if (prevItems.some((m) => m.id === createdMessage.id)) {
              return prevChatData
            }
            return {
              ...prevChatData,
              messages: {
                ...prevChatData.messages,
                items: [...prevItems, createdMessage],
              },
            }
          })
        },
        newChatRoom.chatRoom_uid
      )
      deleteSubscriptionRef.current = await deleteMessageSubscriptionAction(
        (deletedMessage) => {
          if (
            !deletedMessage ||
            deletedMessage.messageChatRoomId !== newChatRoom.chatRoom_uid
          ) {
            return
          }
          // Directly remove deleted message from local chatData.
          getChatData((prevChatData) => {
            if (!prevChatData || Object.keys(prevChatData).length === 0) {
              return prevChatData
            }
            return {
              ...prevChatData,
              messages: {
                ...prevChatData.messages,
                items: (prevChatData.messages?.items || []).filter(
                  (m) => m.id !== deletedMessage.id
                ),
              },
            }
          })
        },
        newChatRoom.chatRoom_uid
      )
    }

    subscribeChatData()
    setStopScrollingToBottom(false)

    // Unsubscribe Chat message
    return () => {
      if (createSubscriptionRef.current) {
        createSubscriptionRef.current.unsubscribe()
        createSubscriptionRef.current = null
      }
      if (deleteSubscriptionRef.current) {
        deleteSubscriptionRef.current.unsubscribe()
        deleteSubscriptionRef.current = null
      }
      window.scrollTo(0, 0)
    }
  }, [
    createMessageSubscriptionAction,
    deleteMessageSubscriptionAction,
    newChatRoom.chatRoom_uid,
  ])

  const setChatMessage = (e) => {
    setMessage(e.target.value)
  }

  const submitForm = (e) => {
    if ((window.e ? e.keyCode : e.which) === 13 && !e.shiftKey) {
      sendMessage(e)
    }
  }

  // A send the server refused (actions/chat.js throws refusals): the upgrade popup when
  // starting a chat is for members only, otherwise a fixed message; anything else (network,
  // throttling, a failed existing-room check) asks to try again. The typed text is kept.
  // Known gap: if createChatRoom succeeded and the first createChatMessage was then refused, that
  // room stays without members or messages and a retry creates another one (no ChatRoomUser rows
  // yet, so checkExistingChatRoom can't find it). Harmless but untidy.
  const sendMessage = async (e) => {
    e.preventDefault()
    setSendingMessage(true)
    try {
      await sendMessageNow()
    } catch (err) {
      console.log('error sending chat message: ', err)
      const refusal = refusalOf(err)
      if (refusal === MEMBERS_ONLY) setUpgradePopup(true)
      else if (refusal) alert("You can't send messages in this chat.")
      else alert("Couldn't send the message. Please try again.")
    } finally {
      // Every path ends here, so the form is never left disabled.
      setSendingMessage(false)
    }
  }

  const sendMessageNow = async () => {
    // Don't send if there's no massage but spaces
    if (!message.replace(/\s/g, '').length) {
      alert('Type something to send')
      return
    }

    // If non-member, show upgrade popup
    if (!membership) {
      setUpgradePopup(true)
      setMessage('')
      return
    }

    setStopScrollingToBottom(false)

    if (newChatRoom.chatRoom_uid && newChatRoom.chatRoom_uid === 'new') {
      if (
        !props.history.location.state &&
        !props.history.location.state.chatWith
      ) {
        //if no chatWith state is passed, no users to chat with
        alert('No user to chat with')
        return
      } else {
        // Create a ChatRoom, ChatRoomUser, and Message

        // TODO: Group Chat in the future
        // User will be able great a group chat with multiple users. Save all users to `allUsers` as String

        const allUsers = props.history.location.state.chatWith // const allUsers = "currentUsername, user4, user3, user2, user1"
        const sortedMembersToChatArray = allUsers.split(', ')
        const AllSortedMembersToChat = sortedMembersToChatArray
          .sort()
          .join(', ') // members must be sorted alphabetically
        const existingChatRooms = await props.checkExistingChatRoom(
          currentUsername,
          AllSortedMembersToChat
        )
        // undefined: the check itself failed (network, throttling). Don't create a second room
        // with the same members on a guess; the user can send again.
        if (!existingChatRooms) {
          throw new Error('could not check for an existing chat room')
        }

        if (existingChatRooms.items.length === 0) {
          const memberIds = memberIdsOf(AllSortedMembersToChat, [currentUsername])
          const chatRoomCreated = await props.createChatRoom(currentUsername, memberIds)
          await props.createChatMessage(
            currentUsername,
            chatRoomCreated.id,
            message,
            memberIds
          )
          sortedMembersToChatArray.map(async (user) => {
            return await props.createChatRoomUser(
              user,
              chatRoomCreated.id,
              AllSortedMembersToChat
            ) // All other users to chat with
          })

          props.history.push('/chat/' + chatRoomCreated.id)
        } else {
          props.history.push(
            '/chat/' + existingChatRooms.items[0].chatRoomUserChatRoomId
          )
        }
      }
    } else if (newChatRoom.chatRoom_uid && Object.keys(chatData).length > 0) {
      // Add message to existing chatRoom
      const messageCreated = await props.createChatMessage(
        currentUsername,
        newChatRoom.chatRoom_uid,
        message,
        roomMemberIds(chatData, currentUsername)
      )

      // Ensure sender sees the new message immediately even if subscription is delayed.
      if (messageCreated) {
        getChatData((prevChatData) => {
          if (!prevChatData || Object.keys(prevChatData).length === 0) {
            return prevChatData
          }

          const prevItems = prevChatData.messages?.items || []
          const exists = prevItems.some((m) => m.id === messageCreated.id)
          if (exists) {
            return prevChatData
          }

          return {
            ...prevChatData,
            messages: {
              ...prevChatData.messages,
              items: [...prevItems, messageCreated],
            },
          }
        })
      }

      // Updating updatedAt under ChatRoomUser table for all users(In order to query ChatRoomUser in chronological order)
      if (!messageCreated) {
        throw new Error('message not created')
      }
      const ChatRoomUserIds = await props.getChatRoomUsersByChatRoomId(
        newChatRoom.chatRoom_uid
      )
      // The message is sent; a failed list-order lookup must not report it as unsent. Only the
      // room's own rows: a row planted in the room by an outsider is skipped (sprint PR 8b).
      for (const row of rowsOfRoom(ChatRoomUserIds?.items, chatData)) {
        props.updateChatRoomUser(row.id, messageCreated.createdAt)
      }

      // Update receiverHasRead to false when oher user send currentUsername a message
      const currentUserSentLastMessage = chatData.messages.items[
        chatData.messages.items.length - 1
      ]
        ? chatData.messages.items[chatData.messages.items.length - 1]
            .messageUserId === currentUsername
        : false
      if (!currentUserSentLastMessage) {
        await props.updateChatRoomRead(newChatRoom.chatRoom_uid, false)
      }
    } else {
      alert("You can't send message.")
    }

    setMessage('')
  }

  const userClickedInput = async () => {
    // Check if the last message is from current user then update receiverHadRead value
    const currentUserSentLastMessage =
      newChatRoom.chatRoom_uid &&
      Object.keys(chatData).length > 0 &&
      (chatData.messages.items[chatData.messages.items.length - 1]
        ? chatData.messages.items[chatData.messages.items.length - 1]
            .messageUserId === currentUsername
        : false)
    if (chatData.receiverHasRead === false && !currentUserSentLastMessage) {
      await props.updateChatRoomRead(newChatRoom.chatRoom_uid, true)
    }
  }

  const popupControl = (callback) => {
    setUpgradePopup(callback)
  }

  const removeChatMessageByUser = async (messageId) => {
    const ask = window.confirm('Remove this message?')
    if (ask) {
      setDeletingMessage(true)
      await props.deleteChatMessage(messageId)
      setTimeout(() => {
        setDeletingMessage(false)
      }, 1000)
    }
  }

  const leaveChatRoom = async () => {
    const ask = window.confirm('Leave this chat room?')
    if (ask) {
      const leaveChatRoom = await props.deleteChatRoomUser(
        newChatRoom.chatRoom_uid,
        currentUsername
      )
      leaveChatRoom && props.history.push('/chat')
    }
  }

  // Show loading stage(I know it's weird but membership here is only used for the loading state)
  if (membership === null) {
    return (
      <div className="Container chat-message-wrapper Container--side-padding">
        <LoadingCard />
      </div>
    )
  }

  const renderChatUI =
    loadFailed && chatData && Object.keys(chatData).length === 0 ? (
      <div className="chat Container">
        <div className="Container Container--sm sm-form no-chat-found">
          <h1>Couldn't load this chat</h1>
          <p>
            <button
              className="btn-secondary"
              onClick={() => setLoadAttempt(loadAttempt + 1)}
            >
              Retry
            </button>
          </p>
        </div>
      </div>
    ) : chatData &&
    Object.keys(chatData).length === 0 &&
    newChatRoom.chatRoom_uid !== 'new' ? (
      <div className="chat Container">
        <LoadingCard />
      </div>
    ) : chatData === null ? (
      <div className="chat Container">
        <div className="Container Container--sm sm-form no-chat-found">
          <h1>
            <span role="img" aria-label="Sweat">
              😅
            </span>
            <br />
            No chat found here
          </h1>
          <p>
            <button
              className="btn-secondary"
              onClick={() => props.history.goBack()}
            >
              Go back
            </button>
          </p>
        </div>
      </div>
    ) : (
      <React.Fragment>
        <div
          id="chat_message_wrapper"
          className="Container chat-message-wrapper Container--side-padding"
        >
          <p className="chat-alert">
            <small>
              <span role="img" aria-label="Bell">
                🔔
              </span>{' '}
              Only{' '}
              <span
                className="link-text"
                onClick={() =>
                  props.history.push({ pathname: '/my-account/billing' })
                }
              >
                members
              </span>{' '}
              can send messages
            </small>
          </p>
          {loading ? (
            <div className="load-more-chat-messages">
              <img
                alt="Loading..."
                className="loading-icon"
                src={loadingIcon}
              />
            </div>
          ) : (
            nextToken && (
              <div className="load-more-chat-messages">
                <button className="btn-secondary" onClick={loadMoreMessages}>
                  Load more
                </button>
              </div>
            )
          )}
          {Object.keys(chatData).length > 0 &&
            chatData.messages.items.map((m) => {
              return (
                <ChatMessageItem
                  key={m.id}
                  chatMessage={m}
                  currentUser={currentUsername}
                  removeChatMessage={removeChatMessageByUser}
                  deletingMessage={deletingMessage}
                />
              )
            })}
        </div>

        <div className="chat-form">
          {Object.keys(chatData).length > 0 &&
          chatData.chatRoomUsers.items.length <= 1 ? (
            <div className="Container Container--sm sm-form no-chat-found">
              <h1>
                <span role="img" aria-label="Sweat">
                  😅
                </span>
                <br />
                User left chat room
              </h1>
              <p>You can leave the room and create a new chat.</p>
            </div>
          ) : (
            <form id="chatFrom" onSubmit={sendMessage}>
              <textarea
                rows="1"
                placeholder="Message"
                value={message}
                onChange={setChatMessage}
                required
                id="chat_textarea"
                onKeyDown={submitForm}
                onFocus={userClickedInput}
                disabled={sendingMessage}
              ></textarea>
              <button id="chat_submit" disabled={!message || sendingMessage}>
                Send
              </button>
            </form>
          )}
        </div>
      </React.Fragment>
    )

  // TODO: Show displayNames of users in the room except current user with the link
  const renderChatTitle =
    chatData && Object.keys(chatData).length !== 0
      ? chatData.chatRoomUsers.items
          .map((u) => (u.user.displayName ? u.user.displayName : u.user.id))
          .join(', ').length > 24
        ? chatData.chatRoomUsers.items
            .map((u) => (u.user.displayName ? u.user.displayName : u.user.id))
            .join(', ')
            .slice(0, 24) + '...'
        : chatData.chatRoomUsers.items
            .map((u) => (u.user.displayName ? u.user.displayName : u.user.id))
            .join(', ')
      : props.history.location.state &&
        props.history.location.state.chatWithDisplayname.length > 24
      ? props.history.location.state &&
        props.history.location.state.chatWithDisplayname.slice(0, 24) + '...'
      : props.history.location.state &&
        props.history.location.state.chatWithDisplayname

  return (
    <div className="chat-room">
      <div>
        <div className="chat-header">
          <ul className="sub-nav sub-nav-back chat-ui-top">
            <li>
              <span
                className="link-text trackit chat-room-goback"
                onClick={() => props.history.goBack()}
                id="chatRoom_subNav_go_back"
              >
                <ion-icon name="arrow-round-back"></ion-icon>
              </span>
            </li>
            <li className="chat-room-title">
              <b>{renderChatTitle}</b>
            </li>
            <li>
              {chatData &&
                Object.keys(chatData).length > 0 &&
                chatData.chatRoomUsers.items.length > 0 && (
                  <span
                    className="link-text trackit"
                    onClick={leaveChatRoom}
                    id="chatRoom_create_a_book"
                  >
                    <ion-icon name="exit"></ion-icon>
                  </span>
                )}
            </li>
          </ul>
        </div>

        {renderChatUI}
      </div>

      <UpgradePopup
        featureName={'Chat'}
        showUpgrade={showUpgrade}
        popupControl={popupControl}
      />
    </div>
  )
}

function mapStateToProps(state) {
  return state
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      checkExistingChatRoom,
      createChatRoom,
      createChatMessage,
      createChatRoomUser,
      getChatRoomData,
      chatCreateMessageSubscriptions,
      chatDeleteMessageSubscriptions,
      getChatRoomUsersByChatRoomId,
      updateChatRoomUser,
      updateChatRoomRead,
      deleteChatMessage,
      deleteChatRoomUser,
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ChatRoom)
